import React, { useState, useEffect } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
  Table,
  Progress,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane
} from "reactstrap";
import { Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import classnames from "classnames";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import {
  AssignStore,
  GetStoreMenu,
  CalculateDeliveryPlayground,
  EvaluatePromotion,
  CreateOrder,
  GetProducts,
  GetVariants,
  GetStores
} from "../../../requests";
import { toast } from "react-toastify";

const extractData = (response) => {
  if (!response) return null;
  if (response.data !== undefined) {
    if (response.data && response.data.success && response.data.data !== undefined) {
      return response.data.data;
    }
    return response.data;
  }
  if (response.success && response.data !== undefined) {
    return response.data;
  }
  return response;
};

export default function OrderLifecycleSimulator() {
  const role = useSelector((state) => state?.login?.role);
  const token = localStorage.getItem("token");

  // General state
  const [loading, setLoading] = useState(false);
  const [simulationActive, setSimulationActive] = useState(false);
  const [simulationResult, setSimulationResult] = useState(null); // 'SUCCESS', 'FAILED'
  const [failureReason, setFailureReason] = useState("");
  const [activeInspectTab, setActiveInspectTab] = useState("session");

  // Step 1 Inputs (Customer Session)
  const [customerType, setCustomerType] = useState("logged-in"); // guest, logged-in, existing, new
  const [customerSession, setCustomerSession] = useState({
    customerId: "CUST-8930",
    sessionToken: "jwt-token-9302183",
    device: "Mobile App (iPhone 15 Pro)",
    platform: "iOS 17.4",
    fcmToken: "fcm-device-token-8291038"
  });

  // Step 2 & 3 Inputs (Location & Store Engine)
  const [lat, setLat] = useState("23.3456");
  const [lng, setLng] = useState("85.3123");
  const [assignedStore, setAssignedStore] = useState(null);
  const [deliveryData, setDeliveryData] = useState(null);
  const [promotionData, setPromotionData] = useState(null);
  const [calculationData, setCalculationData] = useState(null);
  const [orderResponse, setOrderResponse] = useState(null);
  const [isFallbackMenu, setIsFallbackMenu] = useState(false);
  const [storeCoordinates, setStoreCoordinates] = useState(null);

  // Step 4 (Menu)
  const [menuItems, setMenuItems] = useState([]);
  const [menuStats, setMenuStats] = useState(null);
  const [variantTypes, setVariantTypes] = useState([]);

  // Step 5 (Cart & Review)
  const [cart, setCart] = useState([]);
  const [selectedVariants, setSelectedVariants] = useState({}); // { [productId]: variantId }
  const [specialInstructions, setSpecialInstructions] = useState({
    noOnion: false,
    extraCheese: false,
    lessSpicy: false,
    extraSauce: false
  });
  const [couponCode, setCouponCode] = useState("20flat");
  const [addressType, setAddressType] = useState("Home");
  const [deliveryTip, setDeliveryTip] = useState(0);

  // Helper to map variant ID to human-readable label
  const getVariantLabel = (menuItem, variant) => {
    if (!variant) return "";
    const variantId = typeof variant === "string" ? variant : (variant.variantId || variant.id);
    if (!variantId) return "";
    if (variantId === "standard" || variantId === "Regular") return "Standard";

    const productDetails = menuItem?.productDetails;
    if (!productDetails || !productDetails.variantEnabled) {
      return variantId;
    }

    const variantTypeId = productDetails.variantTypeId;
    const variantValueIds = productDetails.variantValueIds || [];

    const vt = variantTypes.find(t => (t.variantId || t.id) === variantTypeId);
    if (!vt) {
      return variantId.charAt(0).toUpperCase() + variantId.slice(1);
    }

    const matchedValue = (vt.values || []).find(
      val => val.toLowerCase() === variantId.toLowerCase()
    );

    if (matchedValue) {
      return matchedValue;
    }

    const matchedValId = variantValueIds.find(
      val => val.toLowerCase() === variantId.toLowerCase()
    );

    if (matchedValId) {
      return matchedValId;
    }

    return variantId.charAt(0).toUpperCase() + variantId.slice(1);
  };

  // Simulation Overrides (16 toggles)
  const [debugOverrides, setDebugOverrides] = useState({
    storeBusy: false,
    storeOffline: false,
    inventorySyncFailed: false,
    taxServiceFailure: false,
    couponExpired: false,
    couponUsageLimit: false,
    productHidden: false,
    variantHidden: false,
    kitchenBusy: false,
    paymentTimeout: false,
    paymentFailed: false,
    firestoreFailure: false,
    notificationFailure: false,
    pushFailure: false,
    deliveryPartnerOffline: false,
    addressNotServiceable: false
  });

  // Pipeline Status (12 Stages)
  // States: 'PENDING', 'RUNNING', 'PASSED', 'FAILED'
  const [pipeline, setPipeline] = useState({
    session: "PENDING",
    location: "PENDING",
    store: "PENDING",
    menu: "PENDING",
    cartSim: "PENDING",
    cartVal: "PENDING",
    delivery: "PENDING",
    promotion: "PENDING",
    calculator: "PENDING",
    payment: "PENDING",
    order: "PENDING",
    notifications: "PENDING"
  });

  // Performance diagnostics timings
  const [timings, setTimings] = useState({
    session: 0,
    location: 0,
    store: 0,
    menu: 0,
    cartSim: 0,
    cartVal: 0,
    delivery: 0,
    promotion: 0,
    calculator: 0,
    payment: 0,
    order: 0,
    notifications: 0
  });

  // Raw JSON Inspector tab payloads
  const [payloads, setPayloads] = useState({
    locationReq: null,
    locationRes: null,
    storeAssignment: null,
    menuResponse: null,
    cartJson: null,
    promotionReq: null,
    promotionRes: null,
    calculatorReq: null,
    orderPayload: null,
    firestoreDoc: null,
    notificationPayload: null
  });

  // Checklist for Cart Validation
  const [validationChecklist, setValidationChecklist] = useState({
    productExists: null, // null = pending, true = success, false = fail
    productActive: null,
    variantExists: null,
    inventoryAvailable: null,
    storeOpen: null,
    customerEligible: null,
    minOrderPassed: null,
    maxQuantityPassed: null,
    deliveryPossible: null,
    addressValid: null
  });

  // Notification Checklist status
  const [notificationStatus, setNotificationStatus] = useState({
    customerPush: "PENDING", // PENDING, SENT, SKIPPED, FAILED
    storePush: "PENDING",
    deliveryPush: "PENDING",
    sms: "PENDING",
    email: "PENDING",
    whatsapp: "PENDING"
  });

  // Update session parameters based on type choice
  useEffect(() => {
    const seed = Math.floor(Math.random() * 10000);
    if (customerType === "guest") {
      setCustomerSession({
        customerId: `GUEST-${seed}`,
        sessionToken: `guest-jwt-${seed}`,
        device: "Desktop Web (Chrome 122)",
        platform: "Windows 11",
        fcmToken: "N/A (Guest session)"
      });
    } else if (customerType === "logged-in") {
      setCustomerSession({
        customerId: `CUST-${seed}`,
        sessionToken: `jwt-user-${seed}`,
        device: "Mobile App (Samsung S24 Ultra)",
        platform: "Android 14",
        fcmToken: `fcm-token-android-${seed}`
      });
    } else if (customerType === "existing") {
      setCustomerSession({
        customerId: "CUST-7740",
        sessionToken: "jwt-user-7740-9081",
        device: "Mobile App (iPhone 15 Pro)",
        platform: "iOS 17.4",
        fcmToken: "fcm-token-ios-7740"
      });
    } else {
      setCustomerSession({
        customerId: `NEW-CUST-${seed}`,
        sessionToken: `jwt-new-user-${seed}`,
        device: "Mobile App (Pixel 8 Pro)",
        platform: "Android 14",
        fcmToken: `fcm-token-pixel-${seed}`
      });
    }
  }, [customerType]);

  // Guard redirect
  if (role === null || role === undefined) return null;
  if (role !== "superadmin") return <Navigate to="/dashboard" />;

  // Trigger: Load initial store settings to render cart simulator
  const initializeStoreSession = async () => {
    setLoading(true);
    try {
      const resp = await AssignStore(parseFloat(lat), parseFloat(lng));
      const data = extractData(resp);
      if (data && data.success) {
        setAssignedStore(data.assignedStore);
        try {
          const storesResp = await GetStores(token);
          const storesData = extractData(storesResp) || [];
          const matchedStore = storesData.find(s => s.id === data.assignedStore.storeId);
          if (matchedStore) {
            const sLat = matchedStore.location?.lat !== undefined ? matchedStore.location.lat : (matchedStore.latitude || 0);
            const sLng = matchedStore.location?.lng !== undefined ? matchedStore.location.lng : (matchedStore.longitude || 0);
            setStoreCoordinates({ lat: Number(sLat), lng: Number(sLng) });
          }
        } catch (e) {
          console.error("Failed to load stores or coordinates:", e);
        }
        try {
          const varResp = await GetVariants();
          const varData = extractData(varResp) || [];
          setVariantTypes(varData);
        } catch (e) {
          console.error("Failed to load variant types in initializeStoreSession:", e);
        }
        const menuResp = await GetStoreMenu(data.assignedStore.storeId);
        let menuData = extractData(menuResp) || [];
        if (menuData.length === 0) {
          setIsFallbackMenu(true);
          try {
            const prodResp = await GetProducts({ status: "active" });
            const prodData = extractData(prodResp) || [];
            const varResp = await GetVariants();
            const varData = extractData(varResp) || [];
            menuData = prodData.map(p => {
              let productVariants = [];
              if (p.variantEnabled) {
                const vt = varData.find(v => (v.variantId || v.id) === p.variantTypeId);
                const values = p.variantValueIds && p.variantValueIds.length > 0
                  ? p.variantValueIds
                  : (vt ? vt.values || [] : ["Regular"]);

                productVariants = values.map(val => ({
                  variantId: val,
                  price: 150,
                  discountPrice: 120,
                  availability: "available",
                  stockStatus: "in stock"
                }));
              } else {
                productVariants = [{
                  variantId: "standard",
                  price: 150,
                  discountPrice: 120,
                  availability: "available",
                  stockStatus: "in stock"
                }];
              }
              return {
                productId: p.productId || p.id,
                productDetails: p,
                variants: productVariants
              };
            });
          } catch (err) {
            console.error("Fallback load catalog failed:", err);
          }
        }
        setMenuItems(menuData);
      }
    } catch (err) {
      console.error("[OrderLifecycleSimulator] Init Error:", err);
    } finally {
      setLoading(false);
    }
  };

  // ─── Cart Handlers ──────────────────────────────────────────────────────────
  const addToCart = (menuItem, variant) => {
    if (!variant) return;
    const price = variant.discountPrice != null ? variant.discountPrice : variant.price;
    setCart(prev => {
      const existing = prev.findIndex(
        c => c.productId === menuItem.productId && c.variantId === variant.variantId
      );
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = { ...updated[existing], quantity: updated[existing].quantity + 1 };
        return updated;
      }
      return [...prev, {
        productId: menuItem.productId,
        variantId: variant.variantId,
        name: menuItem.productDetails?.name || menuItem.productId,
        variantName: getVariantLabel(menuItem, variant),
        price,
        quantity: 1
      }];
    });
    toast.success(`Added ${menuItem.productDetails?.name} (${getVariantLabel(menuItem, variant)}) to cart`);
  };

  const updateQuantity = (idx, qty) => {
    if (qty <= 0) {
      setCart(prev => prev.filter((_, i) => i !== idx));
    } else {
      setCart(prev => prev.map((item, i) => i === idx ? { ...item, quantity: qty } : item));
    }
  };

  const removeFromCart = (idx) => {
    setCart(prev => prev.filter((_, i) => i !== idx));
  };

  const clearCart = () => { setCart([]); setSelectedVariants({}); };


  const handleCopyJson = (payload) => {
    if (!payload) return;
    navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    toast.success("JSON copied to clipboard!");
  };

  const handleDownloadJson = (filename, payload) => {
    if (!payload) return;
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}.json`;
    a.click();
    toast.success(`Downloaded ${filename}.json`);
  };

  const runSimulation = async () => {
    setSimulationActive(true);
    setSimulationResult(null);
    setFailureReason("");
    setLoading(true);
    let isFallback = false;

    // Reset status tracking
    setPipeline({
      session: "PENDING",
      location: "PENDING",
      store: "PENDING",
      menu: "PENDING",
      cartSim: "PENDING",
      cartVal: "PENDING",
      delivery: "PENDING",
      promotion: "PENDING",
      calculator: "PENDING",
      payment: "PENDING",
      order: "PENDING",
      notifications: "PENDING"
    });

    setTimings({
      session: 0,
      location: 0,
      store: 0,
      menu: 0,
      cartSim: 0,
      cartVal: 0,
      delivery: 0,
      promotion: 0,
      calculator: 0,
      payment: 0,
      order: 0,
      notifications: 0
    });

    setValidationChecklist({
      productExists: null,
      productActive: null,
      variantExists: null,
      inventoryAvailable: null,
      storeOpen: null,
      customerEligible: null,
      minOrderPassed: null,
      maxQuantityPassed: null,
      deliveryPossible: null,
      addressValid: null
    });

    setNotificationStatus({
      customerPush: "PENDING",
      storePush: "PENDING",
      deliveryPush: "PENDING",
      sms: "PENDING",
      email: "PENDING",
      whatsapp: "PENDING"
    });

    const sleep = (ms) => new Promise(r => setTimeout(r, ms));
    const startOverall = performance.now();

    // ─── STEP 1: Customer Session ──────────────────────────────────────────
    setPipeline(p => ({ ...p, session: "RUNNING" }));
    await sleep(400);
    const t1 = performance.now();
    const sessionTime = (performance.now() - startOverall).toFixed(1);
    setTimings(t => ({ ...t, session: parseFloat(sessionTime) }));
    setPipeline(p => ({ ...p, session: "PASSED" }));

    // ─── STEP 2: Location Engine ────────────────────────────────────────────
    setPipeline(p => ({ ...p, location: "RUNNING" }));
    await sleep(400);
    const tLocStart = performance.now();
    const locReqPayload = { latitude: parseFloat(lat), longitude: parseFloat(lng) };
    setPayloads(prev => ({ ...prev, locationReq: locReqPayload }));

    // Check addressServiceable override
    if (debugOverrides.addressNotServiceable || debugOverrides.noStoreFound) {
      setPipeline(p => ({ ...p, location: "FAILED" }));
      setSimulationResult("FAILED");
      setFailureReason("Customer address is outside serviceable territories / No Store Found.");
      toast.error("Simulation Failed: Address not serviceable.");
      setLoading(false);
      return;
    }

    const locResp = await AssignStore(parseFloat(lat), parseFloat(lng));
    const locData = extractData(locResp);
    const locTime = (performance.now() - tLocStart).toFixed(1);
    setTimings(t => ({ ...t, location: parseFloat(locTime) }));
    setPayloads(prev => ({ ...prev, locationRes: locData }));

    if (!locData || !locData.success) {
      setPipeline(p => ({ ...p, location: "FAILED" }));
      setSimulationResult("FAILED");
      setFailureReason("Location Engine returned success: false");
      setLoading(false);
      return;
    }
    setPipeline(p => ({ ...p, location: "PASSED" }));

    // ─── STEP 3: Store Assignment ───────────────────────────────────────────
    setPipeline(p => ({ ...p, store: "RUNNING" }));
    await sleep(350);
    const tStoreStart = performance.now();
    const storeInfo = locData.assignedStore;
    setAssignedStore(storeInfo);
    try {
      const storesResp = await GetStores(token);
      const storesData = extractData(storesResp) || [];
      const matchedStore = storesData.find(s => s.id === storeInfo.storeId);
      if (matchedStore) {
        const sLat = matchedStore.location?.lat !== undefined ? matchedStore.location.lat : (matchedStore.latitude || 0);
        const sLng = matchedStore.location?.lng !== undefined ? matchedStore.location.lng : (matchedStore.longitude || 0);
        setStoreCoordinates({ lat: Number(sLat), lng: Number(sLng) });
      }
    } catch (e) {
      console.error("Failed to load store coordinates in runSimulation Step 3:", e);
    }

    // Check store offline override
    if (debugOverrides.storeOffline) {
      setPipeline(p => ({ ...p, store: "FAILED" }));
      setSimulationResult("FAILED");
      setFailureReason("Store is offline / in maintenance mode (Override Active)");
      toast.error("Simulation Failed: Store offline.");
      setLoading(false);
      return;
    }

    const assignedPayload = {
      storeId: storeInfo.storeId,
      storeName: storeInfo.storeName,
      status: debugOverrides.storeBusy ? "busy" : "active",
      distanceKm: storeInfo.distanceKm,
      openingTime: "00:00",
      closingTime: "23:59"
    };

    setPayloads(prev => ({ ...prev, storeAssignment: assignedPayload }));
    const storeTime = (performance.now() - tStoreStart).toFixed(1);
    setTimings(t => ({ ...t, store: parseFloat(storeTime) }));
    setPipeline(p => ({ ...p, store: "PASSED" }));

    // ─── STEP 4: Store Menu Loading ─────────────────────────────────────────
    setPipeline(p => ({ ...p, menu: "RUNNING" }));
    await sleep(450);
    const tMenuStart = performance.now();
    const menuResp = await GetStoreMenu(storeInfo.storeId);
    let menuData = extractData(menuResp) || [];
    if (menuData.length === 0) {
      isFallback = true;
      setIsFallbackMenu(true);
      try {
        const prodResp = await GetProducts({ status: "active" });
        const prodData = extractData(prodResp) || [];
        const varResp = await GetVariants();
        const varData = extractData(varResp) || [];
        menuData = prodData.map(p => {
          let productVariants = [];
          if (p.variantEnabled) {
            const vt = varData.find(v => (v.variantId || v.id) === p.variantTypeId);
            const values = p.variantValueIds && p.variantValueIds.length > 0
              ? p.variantValueIds
              : (vt ? vt.values || [] : ["Regular"]);

            productVariants = values.map(val => ({
              variantId: val,
              price: 150,
              discountPrice: 120,
              availability: "available",
              stockStatus: "in stock"
            }));
          } else {
            productVariants = [{
              variantId: "standard",
              price: 150,
              discountPrice: 120,
              availability: "available",
              stockStatus: "in stock"
            }];
          }
          return {
            productId: p.productId || p.id,
            productDetails: p,
            variants: productVariants
          };
        });
      } catch (err) {
        console.error("Fallback load catalog failed:", err);
      }
    }
    const menuTime = (performance.now() - tMenuStart).toFixed(1);

    setMenuItems(menuData);
    setMenuStats({
      total: menuData.length,
      categories: [...new Set(menuData.map(m => m.productDetails?.categoryId))].length,
      variants: menuData.reduce((sum, item) => sum + (item.variants?.length || 0), 0)
    });
    setPayloads(prev => ({ ...prev, menuResponse: menuData }));
    setTimings(t => ({ ...t, menu: parseFloat(menuTime) }));
    setPipeline(p => ({ ...p, menu: "PASSED" }));

    // ─── STEP 5: Cart Simulator ─────────────────────────────────────────────
    setPipeline(p => ({ ...p, cartSim: "RUNNING" }));
    await sleep(400);
    const tCartStart = performance.now();

    let simulationCart = [...cart];
    if (simulationCart.length === 0) {
      if (menuData && menuData.length > 0) {
        const defaultProduct = menuData[0];
        const defaultVariant = defaultProduct.variants?.[0];
        if (defaultVariant) {
          const price = defaultVariant.discountPrice !== null ? defaultVariant.discountPrice : defaultVariant.price;
          const defaultItem = {
            productId: defaultProduct.productId,
            variantId: defaultVariant.variantId,
            name: defaultProduct.productDetails?.name || "Pizza",
            variantName: getVariantLabel(defaultProduct, defaultVariant),
            price: price,
            quantity: 1
          };
          simulationCart = [defaultItem];
          setCart(simulationCart);
          toast.info(`Auto-populated empty cart with: ${defaultItem.name}`);
        }
      }
    }

    if (simulationCart.length === 0) {
      setPipeline(p => ({ ...p, cartSim: "FAILED" }));
      setSimulationResult("FAILED");
      setFailureReason("Simulation halted: Shopping cart is empty.");
      toast.warning("Add products to the cart first!");
      setLoading(false);
      return;
    }

    const cartPayload = {
      storeId: storeInfo.storeId,
      items: simulationCart,
      instructions: specialInstructions,
      addressType,
      tip: deliveryTip
    };

    setPayloads(prev => ({ ...prev, cartJson: cartPayload }));
    const cartTime = (performance.now() - tCartStart).toFixed(1);
    setTimings(t => ({ ...t, cartSim: parseFloat(cartTime) }));
    setPipeline(p => ({ ...p, cartSim: "PASSED" }));

    // ─── STEP 6: Cart Validation ────────────────────────────────────────────
    setPipeline(p => ({ ...p, cartVal: "RUNNING" }));
    await sleep(500);
    const tValStart = performance.now();

    const checklist = {
      productExists: !debugOverrides.productHidden,
      productActive: !debugOverrides.storeBusy,
      variantExists: !debugOverrides.variantHidden,
      inventoryAvailable: !debugOverrides.outOfStock,
      storeOpen: !debugOverrides.storeOffline,
      customerEligible: true,
      minOrderPassed: simulationCart.reduce((sum, i) => sum + i.price * i.quantity, 0) >= 0,
      maxQuantityPassed: true,
      deliveryPossible: !debugOverrides.outsideRadius,
      addressValid: !debugOverrides.addressNotServiceable
    };

    setValidationChecklist(checklist);
    const valTime = (performance.now() - tValStart).toFixed(1);
    setTimings(t => ({ ...t, cartVal: parseFloat(valTime) }));

    const anyValFailed = Object.values(checklist).includes(false);
    if (anyValFailed) {
      setPipeline(p => ({ ...p, cartVal: "FAILED" }));
      setSimulationResult("FAILED");
      setFailureReason("Cart Validation Checklist FAILED. (Overrides Active)");
      toast.error("Simulation Failed: Cart validation error.");
      setLoading(false);
      return;
    }
    setPipeline(p => ({ ...p, cartVal: "PASSED" }));

    // ─── STEP 7: Delivery Engine ────────────────────────────────────────────
    setPipeline(p => ({ ...p, delivery: "RUNNING" }));
    await sleep(400);
    const tDelivStart = performance.now();

    if (debugOverrides.deliveryPartnerOffline) {
      setPipeline(p => ({ ...p, delivery: "FAILED" }));
      setSimulationResult("FAILED");
      setFailureReason("Delivery service unreachable: Shadowfax / Dunzo operators offline.");
      setLoading(false);
      return;
    }

    const subtotal = simulationCart.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const delivResp = await CalculateDeliveryPlayground(token, {
      storeId: storeInfo.storeId,
      distance: storeInfo.distanceKm,
      cartSubtotal: subtotal,
      deliveryType: "DELIVERY"
    });
    const delivData = extractData(delivResp);
    const delivTime = (performance.now() - tDelivStart).toFixed(1);
    setTimings(t => ({ ...t, delivery: parseFloat(delivTime) }));

    if (debugOverrides.freeDelivery && delivData) {
      delivData.deliveryCharge = 0;
      delivData.freeDelivery = true;
    }

    setDeliveryData(delivData);
    setPipeline(p => ({ ...p, delivery: "PASSED" }));

    // ─── STEP 8: Promotion Engine ───────────────────────────────────────────
    setPipeline(p => ({ ...p, promotion: "RUNNING" }));
    await sleep(450);
    const tPromoStart = performance.now();

    const formattedItems = simulationCart.map(item => ({
      productId: item.productId,
      quantity: item.quantity,
      price: item.price
    }));

    const promoReq = {
      couponCode: debugOverrides.couponExpired ? "EXPIRED_CODE" : couponCode,
      customer: { email: "qa@crework.in" },
      cartItems: formattedItems
    };
    setPayloads(prev => ({ ...prev, promotionReq: promoReq }));

    if (debugOverrides.couponUsageLimit) {
      setPipeline(p => ({ ...p, promotion: "FAILED" }));
      setSimulationResult("FAILED");
      setFailureReason("Promotion Evaluation Failed: Coupon usage limit exceeded.");
      setLoading(false);
      return;
    }

    let promoData = null;
    try {
      const promoResp = await EvaluatePromotion(promoReq);
      promoData = extractData(promoResp);
      setPromotionData(promoData);
      setPayloads(prev => ({ ...prev, promotionRes: promoData }));
    } catch (err) {
      setPromotionData({ success: false, reward: { discountAmount: 0, message: "Evaluation Exception" } });
    }

    const promoTime = (performance.now() - tPromoStart).toFixed(1);
    setTimings(t => ({ ...t, promotion: parseFloat(promoTime) }));
    setPipeline(p => ({ ...p, promotion: "PASSED" }));

    // ─── STEP 9: Price Calculator ───────────────────────────────────────────
    setPipeline(p => ({ ...p, calculator: "RUNNING" }));
    await sleep(350);
    const tCalcStart = performance.now();

    if (debugOverrides.taxServiceFailure) {
      setPipeline(p => ({ ...p, calculator: "FAILED" }));
      setSimulationResult("FAILED");
      setFailureReason("Tax Calculation Engine Exception (Override Active)");
      setLoading(false);
      return;
    }

    const deliveryCharge = delivData ? delivData.deliveryCharge : 0;
    const discount = promoData && promoData.success ? (promoData.reward?.discountAmount || 0) : (promotionData && promotionData.success ? promotionData.reward.discountAmount : 0);
    const tax = 0;
    const packing = 0;
    const grandTotal = Math.max(0, subtotal + deliveryCharge + parseFloat(deliveryTip) - discount);

    const calcPayload = {
      subtotal,
      tax,
      packing,
      deliveryCharge,
      discount,
      grandTotal,
      savings: discount
    };

    setCalculationData(calcPayload);
    setPayloads(prev => ({ ...prev, calculatorReq: calcPayload }));
    const calcTime = (performance.now() - tCalcStart).toFixed(1);
    setTimings(t => ({ ...t, calculator: parseFloat(calcTime) }));
    setPipeline(p => ({ ...p, calculator: "PASSED" }));

    // ─── STEP 10: Payment Simulation ────────────────────────────────────────
    setPipeline(p => ({ ...p, payment: "RUNNING" }));
    await sleep(550);
    const tPayStart = performance.now();

    if (debugOverrides.paymentFailed || debugOverrides.paymentTimeout) {
      setPipeline(p => ({ ...p, payment: "FAILED" }));
      setSimulationResult("FAILED");
      setFailureReason("Gateway Transaction Rejected: Payment timeout or verification error.");
      setLoading(false);
      return;
    }

    const payTime = (performance.now() - tPayStart).toFixed(1);
    setTimings(t => ({ ...t, payment: parseFloat(payTime) }));
    setPipeline(p => ({ ...p, payment: "PASSED" }));

    // ─── STEP 11: Order Creation ────────────────────────────────────────────
    setPipeline(p => ({ ...p, order: "RUNNING" }));
    await sleep(600);
    const tOrderStart = performance.now();

    if (debugOverrides.firestoreFailure) {
      setPipeline(p => ({ ...p, order: "FAILED" }));
      setSimulationResult("FAILED");
      setFailureReason("Firestore Transactions Write Failed (Override Active)");
      setLoading(false);
      return;
    }

    const finalStoreId = isFallback ? "TS2482" : storeInfo.storeId;
    const finalStoreLat = isFallback ? 23.3248066 : (storeCoordinates?.lat || parseFloat(lat));
    const finalStoreLng = isFallback ? 85.3706053 : (storeCoordinates?.lng || parseFloat(lng));
    const finalStoreName = isFallback ? "Test Store" : storeInfo.storeName;
    const finalStoreAddress = isFallback ? "Test Store Address" : storeInfo.address;

    const finalCustLat = isFallback ? 23.3248066 : parseFloat(lat);
    const finalCustLng = isFallback ? 85.3706053 : parseFloat(lng);

    const orderPayload = {
      method: "delivery",
      storeId: finalStoreId,
      store: {
        name: finalStoreName,
        location: { lat: finalStoreLat, lng: finalStoreLng },
        address: finalStoreAddress,
        phoneNumber: "9876543210"
      },
      customerName: "Simulator Test",
      phoneNumber: "9999999999",
      customerEmail: "qa@crework.in",
      customerAddress: "Test House, Bypass Road",
      customerLocation: { latitude: finalCustLat, longitude: finalCustLng },
      items: simulationCart.map(item => ({
        productId: item.productId,
        variantId: item.variantId,
        name: item.name,
        quantity: item.quantity
      })),
      discountAmount: discount,
      totalprice: grandTotal,
      status: "ordered",
      couponsUsed: promoData && promoData.success && promoData.promotion?.id
        ? { id: promoData.promotion.id }
        : (promotionData && promotionData.success && promotionData.promotion?.id ? { id: promotionData.promotion.id } : null)
    };
    setPayloads(prev => ({ ...prev, orderPayload }));

    try {
      const orderResp = await CreateOrder(token, orderPayload);
      const orderData = extractData(orderResp);
      setOrderResponse(orderData);
      setPayloads(prev => ({ ...prev, firestoreDoc: orderData }));
    } catch (err) {
      console.error("[OrderLifecycleSimulator] Order creation error:", err);
      setPipeline(p => ({ ...p, order: "FAILED" }));
      setSimulationResult("FAILED");

      let errMsg = "Order creation failed.";
      // The global axios interceptor rejects with a plain string, handle that first
      if (typeof err === 'string') {
        errMsg = err;
      } else if (err?.response?.data) {
        const data = err.response.data;
        // Flat format: { success: false, error: "..." }
        if (typeof data.error === 'string') {
          errMsg = data.error;
          // Old nested format: { error: { code: { error: "..." }, message: ... } }
        } else if (data.error?.code?.error && typeof data.error.code.error === 'string') {
          errMsg = data.error.code.error;
        } else if (data.error?.error && typeof data.error.error === 'string') {
          errMsg = data.error.error;
        } else if (typeof data.message === 'string' && data.message) {
          errMsg = data.message;
        }
      } else if (err?.message) {
        errMsg = err.message;
      }

      setFailureReason(errMsg);
      setLoading(false);
      return;
    }

    const orderTime = (performance.now() - tOrderStart).toFixed(1);
    setTimings(t => ({ ...t, order: parseFloat(orderTime) }));
    setPipeline(p => ({ ...p, order: "PASSED" }));

    // ─── STEP 12: Notifications ─────────────────────────────────────────────
    setPipeline(p => ({ ...p, notifications: "RUNNING" }));
    await sleep(400);
    const tNotifyStart = performance.now();

    if (debugOverrides.notificationFailure) {
      setNotificationStatus({
        customerPush: "FAILED",
        storePush: "FAILED",
        deliveryPush: "FAILED",
        sms: "FAILED",
        email: "FAILED",
        whatsapp: "FAILED"
      });
      setPipeline(p => ({ ...p, notifications: "FAILED" }));
      setSimulationResult("FAILED");
      setFailureReason("Notification API Dispatch Failed (Override Active)");
      setLoading(false);
      return;
    }

    setNotificationStatus({
      customerPush: "SENT",
      storePush: "SENT",
      deliveryPush: "SENT",
      sms: "SENT",
      email: "SENT",
      whatsapp: "SKIPPED"
    });

    setPayloads(prev => ({
      ...prev,
      notificationPayload: {
        timestamp: new Date().toISOString(),
        channels: ["push", "sms", "email"],
        storeToken: "store-fcm-token-resolved",
        customerEmail: "qa@crework.in"
      }
    }));

    const notifyTime = (performance.now() - tNotifyStart).toFixed(1);
    setTimings(t => ({ ...t, notifications: parseFloat(notifyTime) }));
    setPipeline(p => ({ ...p, notifications: "PASSED" }));

    setSimulationResult("SUCCESS");
    toast.success("Complete Order Lifecycle Simulation Passed!");
    setLoading(false);
  };



  const handleEvaluatePromotion = async () => {
    if (cart.length === 0) {
      toast.warning("Cart is empty!");
      return;
    }
    setLoading(true);
    try {
      const formattedItems = cart.map(item => ({
        productId: item.productId,
        quantity: item.quantity,
        price: item.price
      }));
      const payload = {
        couponCode: couponCode,
        customer: { email: "qa@crework.in" },
        cartItems: formattedItems
      };
      const resp = await EvaluatePromotion(payload);
      const data = extractData(resp);
      setPromotionData(data);
      setPayloads(prev => ({ ...prev, promotionRes: data }));
      toast.success("Coupon evaluated successfully!");
    } catch (err) {
      console.error("[OrderLifecycleSimulator] handleEvaluatePromotion error:", err);
      toast.error("Error evaluating coupon.");
    } finally {
      setLoading(false);
    }
  };

  const overallTime = (
    Object.values(timings).reduce((sum, val) => sum + (Number(val) || 0), 0)
  ).toFixed(1);

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          title="Developer Tools"
          breadcrumbItem="Order Lifecycle Simulator"
        />

        {/* ── VISUAL PIPELINE TRACE ───────────────────────────────────── */}
        <Card className="shadow-sm border-0 mb-4">
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="mb-0 fw-bold text-dark">Order Journey Pipeline</h5>
              <Button
                color="primary"
                className="fw-bold px-4"
                onClick={runSimulation}
                disabled={loading}
              >
                {loading ? <Spinner size="sm" className="me-2" /> : "Run Complete Order Simulation"}
              </Button>
            </div>

            <div className="d-flex flex-wrap gap-2 justify-content-between">
              {[
                { key: "session", label: "Session" },
                { key: "location", label: "Location" },
                { key: "store", label: "Store Assignment" },
                { key: "menu", label: "Menu Loading" },
                { key: "cartSim", label: "Cart Sim" },
                { key: "cartVal", label: "Validation" },
                { key: "delivery", label: "Delivery Engine" },
                { key: "promotion", label: "Promotion" },
                { key: "calculator", label: "Calculator" },
                { key: "payment", label: "Payment" },
                { key: "order", label: "Order Creation" },
                { key: "notifications", label: "Notifications" }
              ].map((stage, i) => (
                <div key={stage.key} className="text-center p-2 rounded flex-fill" style={{
                  background: pipeline[stage.key] === "PASSED" ? "#d1e7dd" : pipeline[stage.key] === "FAILED" ? "#f8d7da" : pipeline[stage.key] === "RUNNING" ? "#fff3cd" : "#f8f9fa",
                  border: pipeline[stage.key] === "PASSED" ? "1px solid #badbcc" : pipeline[stage.key] === "FAILED" ? "1px solid #f5c2c7" : "1px solid #eff2f7"
                }}>
                  <span className="fw-bold d-block small" style={{
                    color: pipeline[stage.key] === "PASSED" ? "#0f5132" : pipeline[stage.key] === "FAILED" ? "#842029" : "#664d03"
                  }}>{stage.label}</span>
                  <Badge color={pipeline[stage.key] === "PASSED" ? "success" : pipeline[stage.key] === "FAILED" ? "danger" : pipeline[stage.key] === "RUNNING" ? "warning" : "secondary"} className="small px-2 py-1">
                    {pipeline[stage.key]}
                  </Badge>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>

        {/* ── FINAL SUMMARY ───────────────────────────────────────────── */}
        {simulationResult && (
          <Card className={classnames("shadow-sm border-0 mb-4 text-white", {
            "bg-success bg-opacity-75": simulationResult === "SUCCESS",
            "bg-danger bg-opacity-75": simulationResult === "FAILED"
          })}>
            <CardBody>
              {simulationResult === "SUCCESS" ? (
                <div>
                  <h5 className="fw-bold mb-2">✔ Simulation Success: Order Created Successfully!</h5>
                  <Row className="small">
                    <Col md={3}><strong>Order ID:</strong> {orderResponse?.orderID || orderResponse?.id || "#1002"}</Col>
                    <Col md={3}><strong>collection ID:</strong> {orderResponse?.id || "#1002"}</Col>
                    <Col md={3}><strong>Store:</strong> {assignedStore?.storeName}</Col>
                    <Col md={3}><strong>Items Count:</strong> {cart.length}</Col>
                    <Col md={3}><strong>Amount Paid:</strong> ₹{calculationData?.grandTotal}</Col>
                  </Row>
                </div>
              ) : (
                <div>
                  <h5 className="fw-bold mb-2">❌ Simulation Failed</h5>
                  <p className="mb-0"><strong>Reason:</strong> {failureReason}</p>
                </div>
              )}
            </CardBody>
          </Card>
        )}

        <Row>
          {/* Left Column: Overrides & Simulation steps */}
          <Col lg={7}>
            {/* Override Controls */}
            <Card className="shadow-sm border-0 mb-4">
              <CardBody>
                <h6 className="text-uppercase text-muted fw-bold mb-3">Simulation Boundary Controls</h6>
                <Row>
                  {Object.keys(debugOverrides).map((key) => (
                    <Col md={6} key={key} className="mb-2">
                      <FormGroup check className="form-switch">
                        <Label check className="small fw-semibold">
                          <Input
                            type="checkbox"
                            role="switch"
                            checked={debugOverrides[key]}
                            onChange={(e) => setDebugOverrides({ ...debugOverrides, [key]: e.target.checked })}
                          />{" "}
                          {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        </Label>
                      </FormGroup>
                    </Col>
                  ))}
                </Row>
              </CardBody>
            </Card>

            {/* Step 1: Session config */}
            <Card className="shadow-sm border-0 mb-4">
              <CardBody>
                <h6 className="text-uppercase text-muted fw-bold mb-3">Step 1: Customer Session Parameters</h6>
                <div className="d-flex gap-3 mb-4">
                  {["guest", "logged-in", "existing", "new"].map((type) => (
                    <FormGroup check key={type}>
                      <Label check className="small fw-semibold">
                        <Input
                          type="radio"
                          name="customerType"
                          checked={customerType === type}
                          onChange={() => setCustomerType(type)}
                        />{" "}
                        {type.toUpperCase()}
                      </Label>
                    </FormGroup>
                  ))}
                </div>
                <Table size="sm" borderless className="small text-muted mb-0">
                  <tbody>
                    <tr>
                      <td style={{ width: "30%" }}>Customer ID</td>
                      <td className="fw-bold text-dark">{customerSession.customerId}</td>
                    </tr>
                    <tr>
                      <td>Session Token</td>
                      <td><code>{customerSession.sessionToken}</code></td>
                    </tr>
                    <tr>
                      <td>Device</td>
                      <td>{customerSession.device} ({customerSession.platform})</td>
                    </tr>
                    <tr>
                      <td>FCM Firebase Token</td>
                      <td><code>{customerSession.fcmToken}</code></td>
                    </tr>
                  </tbody>
                </Table>
              </CardBody>
            </Card>

            {/* Step 2 & 3: Location / Store assignment */}
            <Card className="shadow-sm border-0 mb-4">
              <CardBody>
                <h6 className="text-uppercase text-muted fw-bold mb-3">Step 2 & 3: GPS Coordinates & Store Engine</h6>
                <Row className="mb-3">
                  <Col md={6}>
                    <FormGroup>
                      <Label className="small fw-semibold">Latitude</Label>
                      <Input
                        type="number"
                        step="0.0001"
                        value={lat}
                        onChange={(e) => setLat(e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label className="small fw-semibold">Longitude</Label>
                      <Input
                        type="number"
                        step="0.0001"
                        value={lng}
                        onChange={(e) => setLng(e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Button color="info" className="text-white btn-sm" onClick={initializeStoreSession}>
                  Initialize Coordinates Assignment
                </Button>

                {assignedStore && (
                  <div className="p-3 bg-light rounded mt-3">
                    <Table size="sm" borderless className="small mb-0">
                      <tbody>
                        <tr>
                          <td className="text-muted">Nearest Store</td>
                          <td className="fw-bold text-success">{assignedStore.storeName}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Distance</td>
                          <td>{Number(assignedStore.distanceKm).toFixed(2)} KM</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Business Hours</td>
                          <td>00:00 AM - 11:59 PM (Always Open)</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Status</td>
                          <td><Badge color="success">Open</Badge></td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>

            {/* Step 4: Store Menu Loading */}
            {menuItems.length > 0 && (
              <Card className="shadow-sm border-0 mb-4">
                <CardBody>
                  <h6 className="text-uppercase text-muted fw-bold mb-3">Step 4: Store Menu Loading</h6>
                  <Table size="sm" borderless className="small text-muted mb-0">
                    <tbody>
                      <tr>
                        <td style={{ width: "40%" }}>Products Loaded</td>
                        <td className="fw-bold text-dark">{menuItems.length} items</td>
                      </tr>
                      <tr>
                        <td>Categories Count</td>
                        <td>{[...new Set(menuItems.map(m => m.category || m.productDetails?.categoryId))].length}</td>
                      </tr>
                      <tr>
                        <td>Total Variants</td>
                        <td>{menuItems.reduce((sum, item) => sum + (item.variants?.length || 0), 0)} options</td>
                      </tr>
                      <tr>
                        <td>Source Type</td>
                        <td>
                          {isFallbackMenu ? (
                            <Badge color="warning">Fallback Master Catalog</Badge>
                          ) : (
                            <Badge color="success">Assigned Store Menu</Badge>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </CardBody>
              </Card>
            )}

            {/* Step 5: Product Browser with Variant Selection */}
            {menuItems.length > 0 && (
              <Card className="shadow-sm border-0 mb-4">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="text-uppercase text-muted fw-bold mb-0">Step 5: Product Browser</h6>
                    {cart.length > 0 && (
                      <Button size="sm" color="danger" outline onClick={clearCart} className="px-2 py-0">
                        Clear Cart
                      </Button>
                    )}
                  </div>
                  <div style={{ maxHeight: 420, overflowY: "auto" }}>
                    {menuItems.map((item) => {
                      const pid = item.productId;
                      const variants = item.variants || [];
                      const selVid = selectedVariants[pid] || (variants[0]?.variantId);
                      const selVariant = variants.find(v => v.variantId === selVid) || variants[0];
                      const displayPrice = selVariant ? (selVariant.discountPrice != null ? selVariant.discountPrice : selVariant.price) : "—";
                      const inCartQty = cart.filter(c => c.productId === pid).reduce((s, c) => s + c.quantity, 0);
                      return (
                        <div key={pid} className="p-3 mb-2 rounded" style={{ background: "#f8f9fa", border: "1px solid #e9ecef" }}>
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div>
                              <span className="fw-bold text-dark small d-block">{item.productDetails?.name || pid}</span>
                              <span className="text-muted" style={{ fontSize: "0.72rem" }}>{item.productDetails?.description || ""}</span>
                            </div>
                            <div className="text-end">
                              <span className="fw-bold text-success">₹{displayPrice}</span>
                              {inCartQty > 0 && <Badge color="primary" className="ms-2">{inCartQty} in cart</Badge>}
                            </div>
                          </div>
                          {variants.length > 0 && (
                            <div className="d-flex flex-wrap gap-1 mb-2">
                              {variants.map(v => (
                                <button
                                  key={v.variantId}
                                  type="button"
                                  onClick={() => setSelectedVariants(prev => ({ ...prev, [pid]: v.variantId }))}
                                  style={{
                                    fontSize: "0.72rem",
                                    padding: "2px 8px",
                                    borderRadius: 12,
                                    border: selVid === v.variantId ? "2px solid #3b71ca" : "1px solid #ced4da",
                                    background: selVid === v.variantId ? "#e7f0ff" : "white",
                                    color: selVid === v.variantId ? "#3b71ca" : "#555",
                                    fontWeight: selVid === v.variantId ? 700 : 400,
                                    cursor: "pointer"
                                  }}
                                >
                                  {getVariantLabel(item, v)}
                                  {v.discountPrice != null ? ` ₹${v.discountPrice}` : v.price ? ` ₹${v.price}` : ""}
                                </button>
                              ))}
                            </div>
                          )}
                          <Button
                            size="sm"
                            color="success"
                            className="px-3"
                            disabled={!selVariant}
                            onClick={() => addToCart(item, selVariant)}
                          >
                            + Add to Cart
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </CardBody>
              </Card>
            )}
          </Col>

          {/* Right Column: Cart Review, validation checklists, diagnostics, JSON inspector */}
          <Col lg={5}>
            {/* Cart Preview right pane */}
            {cart.length > 0 && (
              <Card className="shadow-sm border-0 mb-4" style={{ borderTop: "4px solid #3b71ca" }}>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="text-uppercase text-muted fw-bold mb-0">Cart Review</h6>
                    <Button size="sm" color="link" className="text-danger p-0" onClick={clearCart}>Clear All</Button>
                  </div>
                  <Table size="sm" borderless className="small mb-2">
                    <tbody>
                      {cart.map((item, idx) => (
                        <tr key={idx}>
                          <td style={{ width: "45%" }}>
                            <span className="fw-bold text-dark d-block">{item.name}</span>
                            <span className="text-muted" style={{ fontSize: "0.72rem" }}>{item.variantName || item.variantId}</span>
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-1">
                              <Button size="xs" color="secondary" outline onClick={() => updateQuantity(idx, item.quantity - 1)} className="px-1 py-0" style={{ lineHeight: 1 }}>−</Button>
                              <span className="fw-bold px-1">{item.quantity}</span>
                              <Button size="xs" color="secondary" outline onClick={() => updateQuantity(idx, item.quantity + 1)} className="px-1 py-0" style={{ lineHeight: 1 }}>+</Button>
                            </div>
                          </td>
                          <td className="text-end align-middle fw-bold">₹{(item.price * item.quantity).toFixed(0)}</td>
                          <td className="text-end align-middle">
                            <Button size="xs" color="link" className="text-danger p-0" onClick={() => removeFromCart(idx)} style={{ fontSize: "0.8rem" }}>✕</Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                    <span className="small fw-bold text-muted">{cart.reduce((s, i) => s + i.quantity, 0)} item(s)</span>
                    <span className="fw-bold text-dark">Total: ₹{cart.reduce((s, i) => s + i.price * i.quantity, 0).toFixed(0)}</span>
                  </div>

                  {/* Instructions Checklist */}
                  <div className="p-3 bg-light rounded mb-3">
                    <h6 className="fw-bold text-uppercase small mb-2">Special Instructions</h6>
                    {Object.keys(specialInstructions).map(key => (
                      <FormGroup check key={key} inline>
                        <Label check className="small">
                          <Input
                            type="checkbox"
                            checked={specialInstructions[key]}
                            onChange={(e) => setSpecialInstructions({ ...specialInstructions, [key]: e.target.checked })}
                          />{" "}
                          {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        </Label>
                      </FormGroup>
                    ))}
                  </div>

                  {/* Delivery details config */}
                  <Row className="mb-3 small">
                    <Col xs={6}>
                      <Label className="fw-bold">Address</Label>
                      <Input type="select" value={addressType} onChange={(e) => setAddressType(e.target.value)} size="sm">
                        <option value="Home">Home</option>
                        <option value="Office">Office</option>
                        <option value="Custom">Custom</option>
                      </Input>
                    </Col>
                    <Col xs={6}>
                      <Label className="fw-bold">Rider Tip (₹)</Label>
                      <Input type="select" value={deliveryTip} onChange={(e) => setDeliveryTip(e.target.value)} size="sm">
                        <option value={0}>₹0</option>
                        <option value={20}>₹20</option>
                        <option value={50}>₹50</option>
                        <option value={100}>₹100</option>
                      </Input>
                    </Col>
                  </Row>

                  {/* Coupon evaluate */}
                  <FormGroup className="mb-3">
                    <Label className="fw-bold small">Coupon</Label>
                    <div className="d-flex gap-2">
                      <Input
                        type="text"
                        placeholder="FREE50"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value)}
                        size="sm"
                      />
                      <Button color="primary" size="sm" onClick={handleEvaluatePromotion}>Apply</Button>
                    </div>
                  </FormGroup>

                  {calculationData && (
                    <Table size="sm" borderless className="small mb-0 align-middle border-top pt-2">
                      <tbody>
                        <tr>
                          <td>Subtotal</td>
                          <td className="text-end">₹{calculationData.subtotal}</td>
                        </tr>
                        <tr>
                          <td>Delivery Charge</td>
                          <td className="text-end">₹{calculationData.deliveryCharge}</td>
                        </tr>
                        <tr>
                          <td>Discount</td>
                          <td className="text-end text-success">- ₹{calculationData.discount}</td>
                        </tr>
                        <tr className="border-top fw-bold text-dark">
                          <td>Grand Total</td>
                          <td className="text-end">₹{calculationData.grandTotal}</td>
                        </tr>
                      </tbody>
                    </Table>
                  )}
                </CardBody>
              </Card>
            )}

            {/* Checklist display Step 6 */}
            {validationChecklist.productExists !== null && (
              <Card className="shadow-sm border-0 mb-4">
                <CardBody>
                  <h6 className="text-uppercase text-muted fw-bold mb-3">Step 6: Cart Validation Checklist</h6>
                  <ul className="list-unstyled mb-0 small">
                    {Object.keys(validationChecklist).map((key) => (
                      <li key={key} className="mb-2 d-flex align-items-center justify-content-between">
                        <span>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</span>
                        {validationChecklist[key] ? (
                          <Badge color="success">PASS</Badge>
                        ) : (
                          <Badge color="danger">FAIL</Badge>
                        )}
                      </li>
                    ))}
                  </ul>
                </CardBody>
              </Card>
            )}

            {/* Step 12 Notifications logs */}
            {notificationStatus.customerPush !== "PENDING" && (
              <Card className="shadow-sm border-0 mb-4">
                <CardBody>
                  <h6 className="text-uppercase text-muted fw-bold mb-3">Step 12: Notification Logs</h6>
                  <Table size="sm" borderless className="small mb-0">
                    <tbody>
                      {Object.keys(notificationStatus).map((key) => (
                        <tr key={key}>
                          <td>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</td>
                          <td className="text-end">
                            <Badge color={notificationStatus[key] === "SENT" ? "success" : "danger"}>
                              {notificationStatus[key]}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </CardBody>
              </Card>
            )}

            {/* Diagnostics progress bars */}
            <Card className="shadow-sm border-0 mb-4">
              <CardBody>
                <h6 className="text-uppercase text-muted fw-bold mb-3">Performance Diagnostics</h6>
                {[
                  { label: "Customer Session", val: timings.session },
                  { label: "Location Engine", val: timings.location },
                  { label: "Store Assignment", val: timings.store },
                  { label: "Menu Loading", val: timings.menu },
                  { label: "Cart Validation", val: timings.cartVal },
                  { label: "Delivery Engine", val: timings.delivery },
                  { label: "Promotion Engine", val: timings.promotion },
                  { label: "Price Calculator", val: timings.calculator },
                  { label: "Payment Sim", val: timings.payment },
                  { label: "Order Creation", val: timings.order },
                  { label: "Notifications", val: timings.notifications }
                ].map((item, idx) => (
                  <div className="mb-2" key={idx}>
                    <div className="d-flex justify-content-between small">
                      <span>{item.label}</span>
                      <strong>{item.val} ms</strong>
                    </div>
                    <Progress value={Math.min(100, item.val / 5)} color={item.val > 100 ? "danger" : "success"} style={{ height: "6px" }} />
                  </div>
                ))}
                <div className="border-top pt-2 mt-3 d-flex justify-content-between fw-bold text-dark small">
                  <span>TOTAL PROCESS LATENCY</span>
                  <span>{overallTime} ms</span>
                </div>
              </CardBody>
            </Card>

            {/* JSON Inspector tabs */}
            <Card className="shadow-sm border-0">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="text-uppercase text-muted fw-bold mb-0">JSON Payloads Inspector</h6>
                  <div className="d-flex gap-2">
                    <Button size="xs" color="secondary" outline onClick={() => handleCopyJson(payloads[activeInspectTab])}>Copy</Button>
                    <Button size="xs" color="secondary" outline onClick={() => handleDownloadJson(activeInspectTab, payloads[activeInspectTab])}>Download</Button>
                  </div>
                </div>

                <Nav tabs className="small flex-nowrap overflow-auto mb-3">
                  {[
                    { key: "locationReq", label: "Loc Req" },
                    { key: "locationRes", label: "Loc Res" },
                    { key: "storeAssignment", label: "Store Info" },
                    { key: "menuResponse", label: "Menu Res" },
                    { key: "cartJson", label: "Cart JSON" },
                    { key: "promotionReq", label: "Promo Req" },
                    { key: "promotionRes", label: "Promo Res" },
                    { key: "calculatorReq", label: "Calc" },
                    { key: "orderPayload", label: "Order Payload" },
                    { key: "firestoreDoc", label: "Firestore Doc" },
                    { key: "notificationPayload", label: "Notifications" }
                  ].map(tab => (
                    <NavItem key={tab.key}>
                      <NavLink
                        className={classnames({ active: activeInspectTab === tab.key }, "px-2 py-1")}
                        onClick={() => setActiveInspectTab(tab.key)}
                        style={{ cursor: "pointer" }}
                      >
                        {tab.label}
                      </NavLink>
                    </NavItem>
                  ))}
                </Nav>

                <div className="bg-dark p-3 rounded text-white" style={{ maxHeight: "300px", overflowY: "auto" }}>
                  <pre className="mb-0 small text-light" style={{ fontSize: "0.75rem", whiteSpace: "pre-wrap" }}>
                    {payloads[activeInspectTab] ? JSON.stringify(payloads[activeInspectTab], null, 2) : "Awaiting Simulation Run..."}
                  </pre>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
